import {
    Card,
    Box,
    MuiTableContainer,
    IconButton,
    MuiTableCell,
    MuiTableRow,
    styled,
    Divider,
    Theme,
    Icon,
} from '../../../base';

export const StyledCard = styled(Card)(
    ({ theme: theme }: { theme: Theme }) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
    })
);

export const StyledCardHeader = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: 'none',
}));

export const StyledHeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

export const VerticalDivider = styled(Divider)(({ theme }) => ({
    height: 16,
    margin: theme.spacing(0, 1),
    alignSelf: 'center',
}));

export const TasksTableContainer = styled(MuiTableContainer)(() => ({
    flex: 1,
    overflow: 'auto',
    padding: 0,
    marginTop: 0,
    maxHeight: '100%',
}));

export const StyledHeaderTitle = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.primary,
    position: 'sticky',
    zIndex: 1,
}));

export const PriorityFlag = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'priority',
})<{ priority?: 'none' | 'medium' | 'high' }>(({ theme, priority }) => ({
    padding: '0 !important',
    width: '20px !important',
    height: '20px !important',
    color:
        priority === 'none'
            ? theme.palette.text.disabled
            : priority === 'medium'
              ? theme.palette.warning.main
              : theme.palette.error.main,
}));

export const AddButton = styled(Box)(({ theme }) => ({
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: theme.direction === 'rtl' ? 'flex-end' : 'flex-start',
    cursor: 'pointer',
    padding: '4px 0',
    '&:hover': {
        opacity: 0.8,
    },
}));

export const ArchiveButton = styled(Box)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    cursor: 'pointer',
    opacity: 0.7,
    '&:hover': {
        opacity: 1,
    },
}));

export const StyledTableRow = styled(MuiTableRow)(({ theme }) => ({
    height: '30px !important',
    maxHeight: '30px !important',
    '& > .MuiTableCell-root': {
        padding: `${theme.spacing(0.5)} ${theme.spacing(0.6)} !important`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        lineHeight: '1.2 !important',
        height: '30px !important',
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

export const StyledTableHeadRow = styled(MuiTableRow)(({ theme }) => ({
    height: '24px !important',
    backgroundColor: theme.palette.common.white,
    '& > .MuiTableCell-root': {
        padding: `${theme.spacing(0.125)} ${theme.spacing(0.75)} !important`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.common.white,
        fontWeight: 500,
        lineHeight: '1 !important',
        height: '24px !important',
    },
    backgroundImage: 'none',
}));

export const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
    padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)} !important`,
    whiteSpace: 'nowrap',
    height: '30px !important',
    lineHeight: '1.2 !important',
}));

export const FormActions = styled(Box)(
    ({ theme: _theme }: { theme: Theme }) => ({
        display: 'flex',
        alignItems: 'center',
        backgroundImage: 'none',
    })
);

export const StyledTasksIconWrapper = styled(IconButton)(({ theme }) => ({
    marginInlineEnd: theme.spacing(1),
    height: '55px',
    width: '55px',
    '&:disabled': {
        backgroundColor: theme.palette.background.mainBackground,
    },
}));

export const StyledTasksIcon = styled(Icon)(({ theme }) => ({
    color: theme.palette.secondary.main,
}));

export const StyledTasksEmptyState = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '10%',
    height: '100%',
    width: '100%',
}));

export const CheckboxCell = styled(MuiTableCell)(({ theme }) => ({
    width: '32px',
    padding: '0 0 0 8px !important',
    height: '30px !important',
    backgroundImage: 'none',
    direction: theme.direction,
}));

export const FlagCell = styled(MuiTableCell)(({ theme }) => ({
    width: '20px',
    padding: '0 !important',
    height: '30px !important',
    backgroundImage: 'none',
    direction: theme.direction,
}));

export const DateCell = styled(MuiTableCell, {
    shouldForwardProp: (prop) => prop !== 'isEditMode',
})<{ isEditMode: boolean }>(({ theme: theme, isEditMode }) => ({
    width: isEditMode ? '120px' : '90px',
    whiteSpace: 'nowrap',
    height: '30px !important',
    padding: '0 8px 0 0 !important',
    backgroundImage: 'none',
    direction: theme.direction,
    // Overrides for small DatePicker inside DateCell
    '& .MuiInputBase-root.MuiInputBase-sizeSmall': {
        paddingRight: '0 !important',
        paddingLeft: '2px !important',
        height: '35px !important',
        boxSizing: 'border-box !important',
        display: 'flex !important',
        alignItems: 'center !important',
        marginBottom: '0 !important',
    },
    '& .MuiInputBase-input.MuiInputBase-inputSizeSmall': {
        fontSize: '0.8125rem !important',
        padding: '6px 0 6px 4px !important',
        width: '100%',
    },
    '& .MuiInputAdornment-positionEnd.MuiInputAdornment-sizeSmall': {
        margin: '0 !important',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        fontSize: '1.1rem !important',
    },
    '& .MuiInputBase-root.MuiInputBase-sizeSmall .MuiInputAdornment-root .MuiIconButton-root.MuiInputAdornment-sizeSmall':
        {
            padding: '0px !important',
            margin: '0 0 0 2px !important',
            width: '20px !important',
            height: '20px !important',
            borderRadius: '12px !important',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            },
        },
    '& .MuiIconButton-sizeMedium': {
        margin: '0 0 0 2px !important',
        width: '25px !important',
        height: '25px !important',
    },
    '& .MuiTextField-root': {
        marginBottom: '0 !important',
        fontSize: '0.8125rem',
    },
}));

export const ActionsCell = styled(MuiTableCell)(() => ({
    width: '24px',
    padding: '0 8px 0 0 !important',
    height: '30px !important',
    backgroundImage: 'none',
}));

export const StyledTableNewRow = styled(MuiTableRow)(({ theme }) => ({
    height: '30px !important',
    maxHeight: '30px !important',
    backgroundColor: theme.palette.action.hover,
    borderRadius: '20px',
    '& > .MuiTableCell-root': {
        padding: `${theme.spacing(0.5)} ${theme.spacing(0.75)} !important`,
        lineHeight: '1.2 !important',
        height: '30px !important',
    },
}));
